import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Users, Receipt, FileSignature, Banknote, ArrowRight, Film, MessageSquare } from "lucide-react";
import api from "../../lib/api";
import { StatusMark } from "../../components/StatusMark";
import { formatDate, formatTime, currency, formatDateShort } from "../../lib/format";

function NeedTile({ icon: Icon, count, label, to, status }) {
  return (
    <Link to={to} className="paper-card p-6 hover:border-brass transition-colors block" data-testid={`need-${label.replace(/\s+/g, "-").toLowerCase()}`}>
      <div className="flex items-center justify-between">
        <Icon size={20} className="text-brass" />
        <span className="font-mono text-3xl text-ink">{count}</span>
      </div>
      <p className="font-serif text-lg text-ink mt-3 leading-tight">{label}</p>
      <div className="mt-2">{count > 0 ? <StatusMark status={status} /> : <StatusMark status="info" labelOverride="Clear" />}</div>
    </Link>
  );
}

export default function ReefDashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <p className="font-sans text-sm text-stone">Loading the Reef…</p>;

  const n = data.needs_today;

  return (
    <div className="space-y-12" data-testid="reef-dashboard">
      <div>
        <p className="overline mb-2">The Reef — Studio Admin</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-ink tracking-tight">What needs you today</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-5">
        <NeedTile icon={Users} count={n.pending_approvals} label="Pending approvals" to="/reef/registrations" status="pending" />
        <NeedTile icon={Film} count={n.pending_submissions} label="Scene proposals" to="/reef/requests" status="pending" />
        <NeedTile icon={MessageSquare} count={n.open_questions} label="Questions & updates" to="/reef/requests" status="scheduled" />
        <NeedTile icon={Receipt} count={n.unpaid_invoices} label="Unpaid invoices" to="/reef/billing" status="due" />
        <NeedTile icon={FileSignature} count={n.overdue_invoices} label="Overdue" to="/reef/billing" status="overdue" />
        <NeedTile icon={Banknote} count={n.classes_ready_to_bill} label="Classes to bill" to="/reef/classes" status="action" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        {/* Upcoming shoot days */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="font-serif text-2xl text-ink">Upcoming shoot days</h2>
          <div className="divide-y divide-hairline border-t border-hairline">
            {data.upcoming_classes.map((c) => (
              <Link
                key={c.id}
                to={`/reef/classes/${c.id}`}
                className="grid grid-cols-12 gap-3 items-center py-4 group"
                data-testid={`reef-class-${c.id}`}
              >
                <div className="col-span-3 sm:col-span-2 font-mono text-sm text-ink">{formatDateShort(c.date)}</div>
                <div className="col-span-5 sm:col-span-5">
                  <p className="font-serif text-lg text-ink leading-tight group-hover:text-brass transition-colors">{c.title}</p>
                  <p className="font-mono text-xs text-stone">{formatTime(c.start_time)} · {c.venue.split(",")[0]}</p>
                </div>
                <div className="col-span-2 font-mono text-xs text-stone text-center">
                  {c.confirmed_count}/{c.capacity} cast
                </div>
                <div className="col-span-2 hidden sm:flex justify-center">
                  <StatusMark status={c.status} />
                </div>
                <div className="col-span-2 sm:col-span-1 flex justify-end">
                  <ArrowRight size={16} className="text-stone group-hover:text-brass transition-colors" />
                </div>
              </Link>
            ))}
            {data.upcoming_classes.length === 0 && <p className="font-sans text-sm text-stone py-4">No classes scheduled.</p>}
          </div>
        </div>

        {/* Recent sides + billing */}
        <div className="space-y-8">
          <div>
            <h2 className="font-serif text-2xl text-ink mb-4">Recently sent sides</h2>
            <div className="space-y-3">
              {data.recent_sides.map((s, i) => (
                <div key={i} className="flex items-start justify-between text-sm" data-testid={`recent-side-${i}`}>
                  <div>
                    <p className="font-serif text-base text-ink leading-tight">{s.title}</p>
                    <p className="font-mono text-xs text-stone">→ {s.target}</p>
                  </div>
                </div>
              ))}
              {data.recent_sides.length === 0 && <p className="font-sans text-sm text-stone">Nothing sent yet.</p>}
            </div>
          </div>

          <div className="paper-card p-6">
            <h2 className="font-serif text-xl text-ink mb-4">Billing summary</h2>
            <div className="space-y-2 font-mono text-sm">
              <div className="flex justify-between">
                <span className="text-stone">Outstanding</span>
                <span className="text-garnet">{currency(data.billing_summary.outstanding)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-stone">Collected</span>
                <span className="text-confirmed">{currency(data.billing_summary.paid_total)}</span>
              </div>
              <div className="flex justify-between border-t border-hairline pt-2">
                <span className="text-stone">Invoices</span>
                <span className="text-ink">{data.billing_summary.invoice_count}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
